import React, { useEffect, useRef, useState } from 'react';
import { HelpCircle, X, Send, ChevronDown, ChevronUp, Maximize2, Minimize2 } from 'lucide-react';
import {
  askManual,
  fetchGuidelines,
  ManualAnswer,
} from '../../services/umaService';
import UmaMessage from './UmaMessage';

// Stable per-panel-session id, generated once and sent on every askManual
// call so the backend's short-lived session cache (117-05) can resolve
// anaphoric follow-ups ("what about that risk" -> the prior question).
const generateSessionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `uma-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

type ChatEntry =
  | { id: string; kind: 'user'; text: string }
  | { id: string; kind: 'answer'; answer: ManualAnswer; question: string };

const UmaChatLauncher: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [guidelines, setGuidelines] = useState<string[]>([]);
  const [guidelinesLoading, setGuidelinesLoading] = useState(false);
  const [guidelinesLoaded, setGuidelinesLoaded] = useState(false);

  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const sessionIdRef = useRef<string | null>(null);
  if (sessionIdRef.current === null) {
    sessionIdRef.current = generateSessionId();
  }

  // Auto-scroll to the latest entry.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [entries, isLoading]);

  // Focus the question box whenever the panel opens.
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // UMA-GUIDE: populate the guidelines list once per panel open, from the
  // live backend constant — never a hardcoded frontend copy.
  useEffect(() => {
    if (!isOpen || guidelinesLoaded) return;
    setGuidelinesLoading(true);
    fetchGuidelines()
      .then((list) => {
        setGuidelines(list);
        setGuidelinesLoaded(true);
      })
      .catch(() => {
        // Non-fatal — the guidelines panel is informational, not blocking.
        setGuidelinesLoaded(true);
      })
      .finally(() => setGuidelinesLoading(false));
  }, [isOpen, guidelinesLoaded]);

  const handleSend = async () => {
    const question = inputValue.trim();
    if (!question || isLoading) return;

    setEntries((prev) => [...prev, { id: `u-${Date.now()}`, kind: 'user', text: question }]);
    setInputValue('');
    setIsLoading(true);
    setError(null);
    setServiceUnavailable(false);

    try {
      const answer = await askManual({
        question,
        session_id: sessionIdRef.current ?? undefined,
      });
      setEntries((prev) => [
        ...prev,
        { id: `a-${Date.now()}`, kind: 'answer', answer, question },
      ]);
    } catch (err: any) {
      if (err?.response?.status === 503) {
        setServiceUnavailable(true);
      } else {
        setError(
          err?.response?.data?.detail || err?.message || 'Something went wrong. Please try again.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="BTN_uma_fab_open"
        onClick={() => setIsOpen(true)}
        className="uma-fab"
        aria-label="Open help assistant"
      >
        <span className="uma-fab-icon">
          <HelpCircle size={22} />
        </span>
        <span className="uma-fab-label">Help</span>
      </button>

      {isOpen && (
        <div
          className={`uma-panel${maximized ? ' uma-panel--max' : ''}`}
          role="dialog"
          aria-modal="false"
          aria-label="Help assistant"
        >
          <div className="uma-panel-header">
            <div className="uma-panel-header-title">
              <HelpCircle size={18} />
              <h3>Help Assistant</h3>
            </div>
            <div className="uma-panel-header-actions">
              <button
                type="button"
                data-testid="BTN_uma_maximize"
                onClick={() => setMaximized((m) => !m)}
                className="uma-panel-max"
                aria-label={maximized ? 'Restore size' : 'Maximize'}
                title={maximized ? 'Restore size' : 'Maximize'}
              >
                {maximized ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
              </button>
              <button
                type="button"
                data-testid="BTN_uma_close"
                onClick={() => setIsOpen(false)}
                className="uma-panel-close"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="uma-guidelines">
            <button
              type="button"
              data-testid="BTN_uma_guidelines_toggle"
              onClick={() => setShowGuidelines((v) => !v)}
              className="uma-guidelines-toggle"
              aria-expanded={showGuidelines}
            >
              <span>What I can help with</span>
              {showGuidelines ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showGuidelines && (
              <ul className="uma-guidelines-list" data-testid="LST_uma_guidelines">
                {guidelinesLoading && <li className="uma-guidelines-loading">Loading…</li>}
                {!guidelinesLoading && guidelines.length === 0 && (
                  <li className="uma-guidelines-loading">Guidelines unavailable right now.</li>
                )}
                {!guidelinesLoading &&
                  guidelines.map((g, i) => <li key={i}>{g}</li>)}
              </ul>
            )}
          </div>

          <div className="uma-message-list" data-testid="LST_uma_messages">
            {entries.length === 0 && !isLoading && (
              <div className="uma-empty-state">
                Ask a question about using AnyGRC — I'll walk you through the manual, step by
                step.
              </div>
            )}

            {entries.map((entry) =>
              entry.kind === 'user' ? (
                <div key={entry.id} className="uma-message uma-message--user" data-testid="MSG_uma_user">
                  <p className="uma-message-text">{entry.text}</p>
                </div>
              ) : (
                <UmaMessage
                  key={entry.id}
                  answer={entry.answer}
                  question={entry.question}
                  sessionId={sessionIdRef.current ?? undefined}
                />
              )
            )}

            {isLoading && (
              <div className="uma-message uma-message--loading" data-testid="MSG_uma_loading">
                <span className="uma-loading-dot" />
                <span className="uma-loading-dot" />
                <span className="uma-loading-dot" />
              </div>
            )}

            {serviceUnavailable && (
              <div className="uma-banner uma-banner--unavailable" data-testid="MSG_uma_unavailable">
                The help assistant isn't configured yet. Please check back later.
              </div>
            )}

            {error && !serviceUnavailable && (
              <div className="uma-banner uma-banner--error" data-testid="MSG_uma_error">
                {error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="uma-input-area">
            <textarea
              ref={inputRef}
              className="uma-input"
              placeholder="Ask how to do something in AnyGRC… (Shift+Enter for a new line)"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading}
              data-testid="INP_uma_question"
            />
            <button
              type="button"
              className="uma-send-btn"
              onClick={handleSend}
              disabled={!inputValue.trim() || isLoading}
              aria-label="Send question"
              data-testid="BTN_uma_send"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default UmaChatLauncher;
